// src/components/block/liveStreamManagment/LiveRecordingEndModal.tsx
//
// Fin d'un « on air » : que faire de l'enregistrement du direct (2026-11-03).
//
// Remplace les deux boîtes génériques (showConfirm puis showAlert) par une seule
// fenêtre en deux temps :
//   1. « Votre direct est enregistré » — ce qu'on a (nom, durée, poids, angles)
//      et le choix : Publier en vidéo, ou Plus tard.
//   2. « Enregistrement conservé » — où il est, ce qu'il coûte dans le quota
//      (jauge), et les gestes possibles : le garder, ouvrir le Stockage, ou le
//      supprimer tout de suite (avec confirmation) pour libérer la place. On peut
//      encore revenir à la publication.
//
// La suppression efface la prise entière : le programme ET ses angles quand le
// direct était en multi-angle — sinon les angles resteraient, orphelins, dans le
// quota.

import React, { useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import {
  CheckCircle2, Upload, Clock, HardDrive, Trash2, X, Layers, FolderOpen, ArrowLeft, Loader2,
} from 'lucide-react';
import {
  RecordingSession,
  computeDuration,
  formatFileSize,
  deleteRecordingSession,
} from '../../../backendServices/liveStreamManagment/api_recording';
import { estSansLimite } from '../../../dataTypes/adminManagment/quota';

type Step = 'ask' | 'kept' | 'confirmDelete' | 'deleting' | 'deleted';

interface LiveRecordingEndModalProps {
  /** L'enregistrement du programme. */
  session        : RecordingSession;
  /** Les autres fichiers de la même prise (angles du multi-angle), rangés. */
  angleSessions  : RecordingSession[];
  channelUuid    : string;
  quotaDiskBytes : number;
  quotaUsedBytes : number;
  onPublish      : () => void;
  onOpenStorage  : () => void;
  /** Après suppression : la régie relit l'historique (estimation du quota). */
  onDeleted      : () => void;
  onClose        : () => void;
}

const LiveRecordingEndModal: React.FC<LiveRecordingEndModalProps> = ({
  session, angleSessions, channelUuid, quotaDiskBytes, quotaUsedBytes,
  onPublish, onOpenStorage, onDeleted, onClose,
}) => {
  const [step,  setStep]  = useState<Step>('ask');
  const [error, setError] = useState<string | null>(null);

  const angleCount  = angleSessions.length;
  const totalBytes  = useMemo(
    () => [session, ...angleSessions].reduce((sum, s) => sum + (s.sizeBytes ?? 0), 0),
    [session, angleSessions],
  );
  const duration    = session.stoppedAt ? computeDuration(session) : null;
  const sizeLabel   = totalBytes > 0 ? formatFileSize(totalBytes) : null;
  const unlimited   = estSansLimite(quotaDiskBytes);
  const quotaKnown  = !unlimited && quotaDiskBytes > 0;
  // La place prise par CE direct dans le quota, et le reste de l'espace occupé.
  const usedFrac    = quotaKnown ? Math.min(1, quotaUsedBytes / quotaDiskBytes) : 0;
  const thisFrac    = quotaKnown ? Math.min(usedFrac, totalBytes / quotaDiskBytes) : 0;

  const busy = step === 'deleting';
  const close = () => { if (!busy) onClose(); };

  const handleDelete = async () => {
    setStep('deleting');
    setError(null);
    try {
      for (const s of [session, ...angleSessions]) {
        await deleteRecordingSession(channelUuid, s.id);
      }
      setStep('deleted');
      onDeleted();
    } catch (err: any) {
      setError(`La suppression a échoué : ${err?.message ?? err}. L'enregistrement est toujours dans Stockage.`);
      setStep('kept');
    }
  };

  // ── Petits blocs ──────────────────────────────────────────────────────────
  const facts = (
    <div className="mt-3 flex flex-wrap gap-2 text-xs">
      {duration && duration !== '—' && (
        <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-1 text-slate-200">
          <Clock className="h-3.5 w-3.5" /> {duration}
        </span>
      )}
      {sizeLabel && (
        <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-1 text-slate-200">
          <HardDrive className="h-3.5 w-3.5" /> {sizeLabel}
        </span>
      )}
      {angleCount > 0 && (
        <span className="inline-flex items-center gap-1 rounded-full bg-violet-500/20 px-2.5 py-1 text-violet-200">
          <Layers className="h-3.5 w-3.5" /> Programme + {angleCount} angle{angleCount > 1 ? 's' : ''}
        </span>
      )}
    </div>
  );

  const recordingCard = (
    <div className="rounded-lg border border-white/10 bg-white/5 px-4 py-3">
      <div className="truncate text-sm font-semibold text-white" title={session.filename}>
        {session.filename}
      </div>
      {facts}
    </div>
  );

  const quotaGauge = unlimited ? (
    <p className="text-xs text-slate-400">Votre espace de stockage est illimité.</p>
  ) : quotaKnown ? (
    <div>
      <div className="mb-1.5 flex justify-between text-xs text-slate-400">
        <span>Espace de stockage utilisé</span>
        <span className="text-slate-300">
          {formatFileSize(quotaUsedBytes)} sur {formatFileSize(quotaDiskBytes)}
        </span>
      </div>
      <div className="relative h-2.5 overflow-hidden rounded-full bg-white/10" aria-hidden="true">
        <div className="absolute inset-y-0 left-0 bg-slate-400/60" style={{ width: `${usedFrac * 100}%` }} />
        <div
          className="absolute inset-y-0 bg-amber-400"
          style={{ left: `${(usedFrac - thisFrac) * 100}%`, width: `${thisFrac * 100}%` }}
        />
      </div>
      {sizeLabel && (
        <div className="mt-1.5 flex items-center gap-1.5 text-xs text-amber-200">
          <span className="inline-block h-2 w-2 rounded-full bg-amber-400" /> Ce direct : {sizeLabel}
        </div>
      )}
    </div>
  ) : null;

  // ── Étapes ────────────────────────────────────────────────────────────────
  let body: React.ReactNode;

  if (step === 'ask') {
    body = (
      <>
        <div className="mb-4 flex items-center gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-500/20">
            <CheckCircle2 className="h-6 w-6 text-emerald-300" />
          </span>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-emerald-300">Direct terminé</div>
            <h2 id="live-end-title" className="text-lg font-semibold text-white">Votre direct est enregistré</h2>
          </div>
        </div>
        {recordingCard}
        <p className="mt-4 text-sm leading-relaxed text-slate-300">
          Publiez-le en vidéo : vos spectateurs le retrouveront sur le portail, en replay.
        </p>
        <div className="mt-6 flex flex-wrap justify-end gap-3">
          <button
            onClick={() => setStep('kept')}
            className="rounded-lg border border-slate-600 px-4 py-2 text-sm font-medium text-slate-200 hover:bg-white/10"
          >
            Plus tard
          </button>
          <button
            onClick={onPublish}
            autoFocus
            className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-500"
          >
            <Upload className="h-4 w-4" /> Publier en vidéo
          </button>
        </div>
      </>
    );
  } else if (step === 'deleted') {
    body = (
      <>
        <div className="mb-4 flex items-center gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-500/20">
            <Trash2 className="h-5 w-5 text-slate-300" />
          </span>
          <h2 id="live-end-title" className="text-lg font-semibold text-white">Enregistrement supprimé</h2>
        </div>
        <p className="text-sm leading-relaxed text-slate-300">
          {sizeLabel ? <>{sizeLabel} libérés dans votre espace de stockage.</> : <>La place est libérée dans votre espace de stockage.</>}
        </p>
        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            autoFocus
            className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-500"
          >
            Fermer
          </button>
        </div>
      </>
    );
  } else {
    // 'kept', 'confirmDelete', 'deleting'
    body = (
      <>
        <div className="mb-4 flex items-center gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sky-500/20">
            <HardDrive className="h-5 w-5 text-sky-300" />
          </span>
          <h2 id="live-end-title" className="text-lg font-semibold text-white">Enregistrement conservé</h2>
        </div>
        {recordingCard}
        <p className="mt-4 text-sm leading-relaxed text-slate-300">
          Il reste dans votre espace de stockage. Vous pourrez le <strong>publier</strong>, le
          {' '}<strong>télécharger</strong> ou le <strong>renommer</strong> à tout moment avec le
          bouton <strong>Stockage</strong> de la régie.
        </p>
        <div className="mt-4">{quotaGauge}</div>
        {step === 'kept' && (
          <p className="mt-3 text-sm leading-relaxed text-slate-400">
            Vous n'en avez pas besoin ? Supprimez-le maintenant
            {sizeLabel ? <> pour libérer <span className="text-slate-200">{sizeLabel}</span></> : null}.
          </p>
        )}

        {error && (
          <div className="mt-4 rounded-lg border border-red-500/50 bg-red-500/10 px-3 py-2 text-sm text-red-200">{error}</div>
        )}

        {(step === 'confirmDelete' || step === 'deleting') ? (
          <div className="mt-5 rounded-lg border border-red-500/60 bg-red-500/10 p-4">
            <p className="text-sm text-red-100">
              <strong>Supprimer définitivement cet enregistrement ?</strong>
              {angleCount > 0 && <> Le programme et ses {angleCount} angle{angleCount > 1 ? 's' : ''} seront effacés.</>}
              {' '}Cette action est irréversible.
            </p>
            <div className="mt-3 flex flex-wrap justify-end gap-3">
              <button
                onClick={() => setStep('kept')}
                disabled={busy}
                className="rounded-lg border border-slate-600 px-4 py-2 text-sm font-medium text-slate-200 hover:bg-white/10 disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={handleDelete}
                disabled={busy}
                autoFocus
                className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-500 disabled:opacity-60"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                {busy ? 'Suppression…' : 'Supprimer'}
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button
              onClick={() => setStep('confirmDelete')}
              className="inline-flex items-center gap-2 rounded-lg border border-red-500/60 px-3 py-2 text-sm font-medium text-red-300 hover:bg-red-500/10"
            >
              <Trash2 className="h-4 w-4" /> Supprimer
            </button>
            <div className="flex-1" />
            <button
              onClick={onOpenStorage}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-600 px-3 py-2 text-sm font-medium text-slate-200 hover:bg-white/10"
            >
              <FolderOpen className="h-4 w-4" /> Ouvrir le Stockage
            </button>
            <button
              onClick={onClose}
              autoFocus
              className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-500"
            >
              Le garder
            </button>
          </div>
        )}

        {step === 'kept' && (
          <button
            onClick={() => setStep('ask')}
            className="mt-4 inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-200"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Finalement, le publier maintenant
          </button>
        )}
      </>
    );
  }

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/65 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="live-end-title"
      onKeyDown={e => { if (e.key === 'Escape') close(); }}
    >
      <div className="relative w-full max-w-lg rounded-xl border border-white/10 bg-slate-900 p-6 text-slate-100 shadow-2xl">
        <button
          onClick={close}
          disabled={busy}
          aria-label="Fermer"
          className="absolute right-3 top-3 rounded-full p-1 text-slate-400 hover:bg-white/10 hover:text-white disabled:opacity-40"
        >
          <X className="h-5 w-5" />
        </button>
        {body}
      </div>
    </div>,
    document.body,
  );
};

export default LiveRecordingEndModal;
